"use client";
import * as Sentry from "@sentry/nextjs";
import NextError from "next/error";
import { useEffect } from "react";
import { LogLevel } from "@axiomhq/logging";
import { useLogger } from "@/lib/axiom/client";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";

/**
 * Extracts the source file and line number from the error stack trace
 * Handles both development and production (minified) stack traces
 */
function parseErrorSource(stack?: string): {
    file: string;
    line: number | null;
    column: number | null;
    rawStack: string;
} {
    if (!stack) return { file: "unknown", line: null, column: null, rawStack: "" };

    // Get the first meaningful line of the stack trace (skip the error message line)
    const stackLines = stack.split("\n");
    
    // Find the first line that contains file information
    let targetLine = "";
    for (let i = 0; i < stackLines.length; i++) {
        const line = stackLines[i].trim();
        // Look for lines with "at" or lines with file paths
        if (line.includes("at ") || line.match(/https?:\/\//) || line.match(/\.tsx?:|\.jsx?:/)) {
            targetLine = line;
            break;
        }
    }

    if (!targetLine) {
        targetLine = stackLines[1] || stackLines[0] || "";
    }

    // Extract file path from various stack trace formats
    // Formats to handle:
    // - "at functionName (file:line:column)" (development)
    // - "at file:line:column" (production)
    // - "functionName@file:line:column" (some browsers)
    // - webpack-internal paths
    
    let filePath = "unknown";
    let lineNumber: number | null = null;
    let columnNumber: number | null = null;

    // Try to match various patterns
    const patterns = [
        // Standard format: at functionName (file:line:column)
        /at\s+(?:.*?\s+)?\((.+?):(\d+):(\d+)\)/,
        // Simplified format: at file:line:column
        /at\s+(.+?):(\d+):(\d+)/,
        // Firefox format: functionName@file:line:column
        /@(.+?):(\d+):(\d+)/,
        // Webpack internal format
        /webpack-internal:\/\/\/(.+?):(\d+):(\d+)/,
        // Generic URL format
        /(https?:\/\/.+?):(\d+):(\d+)/,
    ];

    for (const pattern of patterns) {
        const match = targetLine.match(pattern);
        if (match) {
            filePath = match[1];
            lineNumber = match[2] ? parseInt(match[2], 10) : null;
            columnNumber = match[3] ? parseInt(match[3], 10) : null;
            break;
        }
    }

    // Clean up webpack-internal paths and extract component names
    if (filePath !== "unknown") {
        filePath = filePath
            // Remove webpack internal prefix
            .replace(/webpack-internal:\/\/\/\(app-pages-browser\)\/\.\//, "")
            .replace(/webpack-internal:\/\/\//, "")
            // Remove absolute paths, keep relative paths from project root
            .replace(/.*\/\.next\//, ".next/")
            .replace(/.*\/components\//, "components/")
            .replace(/.*\/app\//, "app/")
            .replace(/.*\/lib\//, "lib/")
            .replace(/.*\/src\//, "src/")
            // Remove query parameters and hashes from URLs
            .replace(/\?.*$/, "")
            .replace(/#.*$/, "")
            // Decode URL encoding
            .replace(/%2F/g, "/")
            .replace(/%20/g, " ");
    }

    return { 
        file: filePath, 
        line: lineNumber, 
        column: columnNumber,
        rawStack: targetLine 
    };
}

export default function ErrorPage({
    error,
}: {
    error: Error & { digest?: string };
}) {
    const pathname = usePathname();
    const log = useLogger();
    let status = error.message == "Invalid URL" ? 404 : 500;

    // Extract source file from stack trace
    const errorSource = parseErrorSource(error.stack);

    log.log(LogLevel.error, error.message, {
        source: "error.tsx",
        error: error.name,
        cause: error.cause,
        stack: error.stack,
        digest: error.digest,
        errorOrigin: {
            file: errorSource.file,
            line: errorSource.line,
            column: errorSource.column,
            rawStackLine: errorSource.rawStack,
        },
        request: {
            host: window.location.href,
            path: pathname,
            statusCode: status,
        },
        environment: {
            nodeEnv: process.env.NODE_ENV,
            userAgent: typeof window !== "undefined" ? window.navigator.userAgent : undefined,
        },
    });
    return (
        <div className="mx-auto flex h-full max-w-lg flex-col items-center justify-center p-8">
            Oops! An Error has occurred{" "}
            <div className="px-8 py-2 text-lg text-destructive">
                <pre className="max-w-prose text-wrap">
                    <code>{error.message}</code>
                </pre>
            </div>
            <div className="mt-8 flex w-1/3 items-center gap-2">
                {/* <a className="w-fit" href="/admin"> */}
                <Button onClick={() => window.location.reload()}>Reload</Button>
                {/* </a> */}
                <a className="w-fit" href="https://doras.to">
                    <Button>Homepage</Button>
                </a>
                <a className="w-fit" href="https://doras.to/discord">
                    <Button>Get help</Button>
                </a>
            </div>
        </div>
    );
}
